/* Tying site photographs to the schools they were taken at.
 *
 * The photo layer has no field pointing at a school. What it has is a name:
 *
 *   AAS_BUS1     AAS_BUS2     AAS_WW1
 *
 * which is the school's own code, an abbreviation for the kind of interface,
 * and a number. AAS is Alfredo Andrews in the schools layer, BUS is school bus
 * access, WW is wastewater. So the link is real and exact, and it is sitting in
 * a string.
 *
 * Nothing here has a list of what the abbreviations mean, deliberately. Two of
 * them are known and there are probably a dozen, and a half guessed lookup
 * would file photographs under the wrong heading with total confidence. The
 * codes are read from the schools layer itself, and whatever follows one is
 * shown as it was written until somebody confirms the vocabulary.
 *
 * Pure.
 */

export const normalise = (v) => String(v ?? "").trim().toUpperCase();

/* The school a photograph belongs to.
 *
 * Matched against the codes the schools layer actually carries rather than
 * against a pattern, because a pattern would happily accept a photograph named
 * after a school that does not exist on this program and quietly file it.
 *
 * The longest matching code wins. GAA and GAP1 are both real school codes, and
 * a shortest-first match would file every Gladys Abraham Phase 3 photograph
 * under Phase 1.
 */
export function codeIn(name, codes) {
  const text = normalise(name);
  if (!text) return null;

  const before = text.includes("_") ? text.slice(0, text.indexOf("_")) : text;
  if (codes.has(before)) return before;

  let best = null;
  for (const code of codes) {
    if (!text.startsWith(code)) continue;
    /* A code has to end where a word ends, so ARPK does not swallow a
       photograph belonging to a school coded ARPKX. */
    const after = text.charAt(code.length);
    if (after && /[A-Z0-9]/.test(after) && !codes.has(text.slice(0, code.length + 1))) {
      if (!/[_\-. ]/.test(after) && code.length < before.length) continue;
    }
    if (!best || code.length > best.length) best = code;
  }
  return best;
}

/* What was written after the code: BUS1, WW1. Shown as it stands, because
   nobody has confirmed what the abbreviations stand for. */
export function subjectIn(name, code) {
  const text = String(name ?? "").trim();
  if (!code) return text;
  const rest = text.slice(code.length).replace(/^[_\-. ]+/, "");
  return rest || text;
}

/* The photographs of each school, and the ones that belong to nothing.
 *
 * A photograph that matches no school is reported rather than dropped. On a
 * program where somebody walks a site with a phone, a name typed slightly
 * wrong is the normal case and silently losing it is how a site ends up
 * looking unvisited.
 */
export function groupPhotos({ photoRows = [], nameKey = "Name", codes = [] } = {}) {
  const known = new Set([...codes].map(normalise).filter(Boolean));
  const byCode = new Map();
  const unmatched = [];

  for (const row of photoRows) {
    const code = codeIn(row[nameKey], known);
    if (!code) { unmatched.push(row); continue; }
    if (!byCode.has(code)) byCode.set(code, []);
    byCode.get(code).push({ ...row, _code: code, _subject: subjectIn(row[nameKey], code) });
  }

  for (const list of byCode.values()) {
    list.sort((a, b) => String(a._subject).localeCompare(
      String(b._subject), undefined, { numeric: true }));
  }

  return { byCode, unmatched, matched: photoRows.length - unmatched.length };
}

/* What the subjects are, across the whole program, so the vocabulary can be
   read off the data rather than assumed. Whoever owns the GIS can then confirm
   what each one stands for. */
export function subjectTally({ photoRows = [], nameKey = "Name", codes = [] } = {}) {
  const { byCode } = groupPhotos({ photoRows, nameKey, codes });
  const tally = new Map();
  for (const list of byCode.values()) {
    for (const photo of list) {
      /* BUS1 and BUS2 are two photographs of one subject. */
      const kind = String(photo._subject).replace(/\d+$/, "") || photo._subject;
      tally.set(kind, (tally.get(kind) ?? 0) + 1);
    }
  }
  return [...tally.entries()]
    .map(([kind, count]) => ({ kind, count }))
    .sort((a, b) => b.count - a.count || a.kind.localeCompare(b.kind));
}

/* Which field on the map layer the photographs are named after.
 *
 * Rather than asking somebody to nominate it, every field that could be a key
 * is tried and the one that accounts for the most photographs wins. On the
 * schools layer that is the school code, and it wins by a distance: the others
 * match nothing at all. If none of them match anything, the answer is that
 * these photographs are not named after this layer, which is worth saying
 * rather than guessing at.
 */
export function bestCodeField({ candidates = [], layerRows = [], photoRows = [], nameKey = "Name" } = {}) {
  let best = null;
  for (const column of candidates) {
    const codes = layerRows.map((r) => r[column.key]);
    const { matched } = groupPhotos({ photoRows, nameKey, codes });
    if (matched && (!best || matched > best.matched)) best = { column, matched };
  }
  return best;
}
